"use client";

import Icon from "@/components/icons/Icons";
import { useState, useRef, useEffect } from "react";

type CustomRangeSliderProps = {
  value?: number | null;
  values?: number[];
  showInput?: boolean;
  inputSuffix?: string;
  minAllowed?: number;
  maxAllowed?: number;
  defaultIndex?: number;
  onChange?: (value: number) => void;
  className?: string;
};

export default function CustomRangeSlider({ value, values = [5, 10, 15, 20, 25, 30], showInput = true, inputSuffix = "Years", minAllowed = 0, maxAllowed = values[values.length - 1], defaultIndex = 0, onChange, className = "" }: CustomRangeSliderProps) {
  const initialIndex = value != null && values.includes(value) ? values.indexOf(value) : defaultIndex;

  const [index, setIndex] = useState(initialIndex);
  const [inputValue, setInputValue] = useState(String(values[initialIndex]));
  const [error, setError] = useState(false);

  const rangeRef = useRef<HTMLInputElement | null>(null);
  const fillRef = useRef<HTMLDivElement | null>(null);
  const thumbRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!trackRef.current) return;

    if (value != null) {
      let targetValue = value;

      if (targetValue < minAllowed) targetValue = minAllowed;
      if (targetValue > maxAllowed) targetValue = maxAllowed;

      const closestValue = values.reduce((prev, curr) => (Math.abs(curr - targetValue) < Math.abs(prev - targetValue) ? curr : prev));

      const newIndex = values.indexOf(closestValue);

      updateSliderUI(newIndex, targetValue);
      setInputValue(String(targetValue));
    } else {
      updateSliderUI(initialIndex);
    }
  }, [trackRef.current]);

  const updateSliderUI = (idx: number, customValue?: number) => {
    if (!trackRef.current || !thumbRef.current || !fillRef.current) return;

    const trackWidth = trackRef.current.offsetWidth;
    const padding = 24;
    const totalUsableWidth = trackWidth - padding * 2;
    const stepGap = totalUsableWidth / (values.length - 1);

    const position = padding + stepGap * idx;

    fillRef.current.style.width = `${position}px`;
    thumbRef.current.style.left = `${position}px`;

    setIndex(idx);
    setError(false);
    onChange?.(customValue ?? values[idx]);
  };

  const onSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newIndex = Number(e.target.value);
    updateSliderUI(newIndex);
    setInputValue(String(values[newIndex]));
  };

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const text = e.target.value.replace(/[^\d]/g, "");

    let parsedValue = text ? Number(text) : 0;
    if (parsedValue > maxAllowed) parsedValue = maxAllowed;

    if (parsedValue === 0) {
      setInputValue("0");
      onChange?.(0);
      setError(true);
      return;
    }

    setInputValue(String(parsedValue));
    setError(false);
    onChange?.(parsedValue);

    const closest = values.reduce((prev, curr) => (Math.abs(curr - parsedValue) < Math.abs(prev - parsedValue) ? curr : prev));
    updateSliderUI(values.indexOf(closest), parsedValue);
  };

  return (
    <div className={`relative w-full space-y-4 ${className}`}>
      <div ref={trackRef} className="relative w-full max-w-80 h-9 bg-[#FFFFFF26] border border-[#FFFFFF33] rounded-full shadow-md">
        <div
          ref={fillRef}
          className="absolute left-0 top-0 h-full rounded-l-full transition-all duration-150"
          style={{
            background: "linear-gradient(90deg, #937101 0%, #DAA000 100%)",
            width: "0%",
          }}
         />

        <div className="absolute inset-0 flex justify-between items-center px-5 pointer-events-none">
          {values.map((_, i) => (
            <div key={i} className="flex items-center">
              <span className="w-3 h-3 bg-white rounded-full border border-white shadow-sm" />
              {i !== values.length - 1 && <span className="h-6 border border-white/20 mx-4.5" />}
            </div>
          ))}
        </div>

        <div ref={thumbRef} className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-10 h-10 flex items-center justify-center bg-[#674E00] border border-white rounded-full shadow-[inset_0px_0.07px_1.49px_0px_#000000] transition-all duration-150 pointer-events-none">
          <Icon.mysipIcon className="text-white w-6 h-6" />
        </div>

        <input ref={rangeRef} type="range" min={0} max={values.length - 1} value={index} step={1} onChange={onSliderChange} className="absolute top-0 left-0 w-full h-full opacity-0 cursor-pointer" />
      </div>

      {showInput && (
        <div className="bg-[#FFFFFF1A] rounded-xl flex items-center gap-4 p-1.5">
          <div className="relative max-w-28 font-medium text-lg bg-[#FFFFFF1A]">
            <input type="text" value={inputValue} onChange={onInputChange} className={`w-full rounded-lg px-5 py-1 border ${error ? "border-[#FF6565] bg-[#ffbaba] text-primaryBlack" : "text-white border-gray-300 bg-transparent"}`} />
            <span className={`absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none ${error ? "text-black/50" : "text-white/50"}`}>{inputSuffix}</span>
          </div>

          <span className="inline-block text-white text-2xs pr-1">Enter Custom Tenure</span>
        </div>
      )}

      {error && <p className="absolute -bottom-5 left-0 text-[#ff7070] text-2xs">*Please select your Tenure between 1 - {maxAllowed}</p>}
    </div>
  );
}
